import { useEffect, useState } from 'react';
import { LogEntry } from '../../../../../types/SystemLogging.types';
import { formatEventDate } from '../../../../../utils/EventFormatting.util';
import { getUserInitials } from '../../../../../utils/Initials.util';
import { ChangeImpactDropdown } from '../../../../ui/ChangeImpactBadge/ChangeImpactDropdown';
import {
  normalizeChangeImpact,
  SupportedChangeImpact,
} from '../../../../ui/ChangeImpactBadge/ChangeImpactBadge';

type AuditLogsTableRowProps = {
  entry: LogEntry;
  onRowClick?: (entry: LogEntry) => void;
  showSeverity?: boolean;
  canEditSeverity?: boolean;
  onSeverityUpdated?: (
    entryId: string,
    impact: SupportedChangeImpact
  ) => Promise<void> | void;
};

const typeLabels: Record<LogEntry['type'], string> = {
  'audit-ai-system': 'AI System',
  'audit-organization': 'Organization',
  global: 'Global',
  'repo-sync': 'Repo Sync',
};

export function AuditLogsTableRow({
  entry,
  onRowClick,
  showSeverity = false,
  canEditSeverity = false,
  onSeverityUpdated,
}: AuditLogsTableRowProps) {
  const normalizedImpact = normalizeChangeImpact(entry.changeImpact);
  const [selectedImpact, setSelectedImpact] =
    useState<SupportedChangeImpact>(normalizedImpact);

  useEffect(() => {
    setSelectedImpact(normalizedImpact);
  }, [normalizedImpact]);

  const handleImpactChange = async (impact: SupportedChangeImpact) => {
    try {
      await onSeverityUpdated?.(entry.id, impact);
      setSelectedImpact(impact);
    } catch (error) {
      console.error('Failed to update change impact:', error);
      alert('Failed to update change impact. Please try again.');
    }
  };

  const actionColumnWidth = showSeverity ? 'w-[32%]' : 'w-[38%]';
  const userColumnWidth = showSeverity ? 'w-[18%]' : 'w-[22%]';
  const typeColumnWidth = showSeverity ? 'w-[16%]' : 'w-[18%]';
  const dateColumnWidth = showSeverity ? 'w-[20%]' : 'w-[22%]';

  return (
    <tr
      onClick={() => onRowClick?.(entry)}
      className="w-full flex flex-row cursor-pointer bg-white border-t border-gray-100 relative overflow-visible hover:bg-gray-50"
    >
      <td
        className={`${actionColumnWidth} min-w-0 flex items-center py-3 px-6 text-sm font-medium text-gray-900v2`}
      >
        <span className="block flex-1 min-w-0 truncate text-left hover:underline">
          {entry.action}
        </span>
      </td>

      <td
        className={`${userColumnWidth} min-w-0 flex items-center py-3 px-6 gap-2 text-sm font-normal text-gray-550`}
      >
        <span className="w-6 h-6 shrink-0 rounded-xl bg-[#EEEEF0] flex items-center justify-center">
          <span className="font-inter text-center font-normal text-xs leading-3 text-[#4E4E56]">
            {getUserInitials(entry.user)}
          </span>
        </span>
        <span className="truncate text-left">{entry.user}</span>
      </td>

      <td className={`${typeColumnWidth} flex items-center py-3 px-6 text-sm font-normal text-gray-550`}>
        <span className="truncate">{typeLabels[entry.type]}</span>
      </td>

      {showSeverity && (
        <td className="w-[14%] flex py-3 px-6 gap-1 text-sm font-normal items-center text-gray-550 relative">
          <ChangeImpactDropdown
            selectedImpact={selectedImpact}
            canEdit={canEditSeverity}
            onSelect={handleImpactChange}
          />
        </td>
      )}

      <td className={`${dateColumnWidth} flex items-center py-3 px-6 text-sm font-normal text-gray-550`}>
        <span className="truncate">{formatEventDate(entry.date)}</span>
      </td>
    </tr>
  );
}
